"use client";
import { Track } from "@/src/lib/tracking";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Member/CurrentPlan/subscriptionRetention/RetentionCreditsOfferModalContent";

import { FC, useEffect } from "react";
import { Icon } from "@ospweb/sdk-ui";
import { cn } from "utils";
import { useMemberGrantEquity } from "@hooks/useMemberGrantEquity";
import { EquityGrantCodeEnum } from "@features/api/memberApi";
import NextImage from "@components/NextImage";

const I18n = I18nUtil.createScoped(Translations);

const FeatureCell: FC<{ icon: string; label: string }> = ({ icon, label }) => (
  <div className="flex flex-col gap-2 rounded-2 bg-background_solid_neutral_primary p-3">
    <div className="flex size-8 shrink-0 items-center justify-center rounded-2 bg-background_white_overlay5">
      <Icon name={icon} size={20} className="text-content_secondary" />
    </div>
    <p className="body_medium_m text-content_primary">{label}</p>
  </div>
);

export interface RetentionCreditsOfferModalContentProps {
  onClose: () => void;
  /** 副标题中的计划名占位，例如「Pro」 */
  planName?: string;
  onClaimCredits?: () => void;
  onStillWantToCancel?: () => void;
}

/**
 * 挽留弹框 Trial/Pro — 500 Credits（Figma 82684:223811，不含 Status Bar / Home Indicator）
 */
export const RetentionCreditsOfferModalContent: FC<
  RetentionCreditsOfferModalContentProps
> = ({ onClose, planName = "Pro", onClaimCredits, onStillWantToCancel }) => {
  const { grantEquity, isGrantEquityPending } = useMemberGrantEquity({
    code: EquityGrantCodeEnum.MEMBER_CANCEL_SUBSCRIPTION_RETENTION_CREDIT,
    enabled: false,
  });
  const claimCredits = async () => {
    void grantEquity();
    onClaimCredits?.();
  };
  useEffect(() => {
    Track.view("member_subscription_retention_credits_offer_modal_view");
  }, []);
  return (
    <div className="flex w-full h-full flex-col pb-4 bg-background_solid_neutral_secondary pt-safe pb-safe">
      <div className="px-4 py-3">
        <Icon
          data-openreplay-label="member_retentioncreditsoffer_close"
          name="close"
          size={24}
          className="text-content_primary"
          onClick={onClose}
        />
      </div>
      <div className="flex flex-1 flex-col gap-6 px-4">
        <div className="flex w-full flex-col gap-2">
          <div className="h-25 pl-1">
            <NextImage
              src="/assets/images/500-credits.svg"
              alt="subscription retention credits offer modal content"
              width={118}
              height={102}
            />
          </div>
          <p className="headline_regular_l text-content_primary">
            {I18n.t("How about 500 Credits on us?")}
          </p>
          <p className="body_m text-content_primary">{`Stay and keep enjoying your ${planName} benefits:`}</p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <FeatureCell
            icon="edgen-radar-outline"
            label={I18n.t("Sector Scanner")}
          />
          <FeatureCell icon="candle-move" label={I18n.t("Alpha Picks")} />
          <FeatureCell icon="file" label={I18n.t("Earnings Plays")} />
          <FeatureCell icon="candles" label={I18n.t("IPO Strategies")} />
        </div>
      </div>
      <div className="flex shrink-0 flex-col gap-3 px-4">
        <button
          type="button"
          data-openreplay-label="member_retentioncreditsoffer_claim"
          data-tracking-eventname="member_subscription_retention_credits_offer_modal_claim_click"
          className={cn(
            "button_m button md_button primary_button w-full bg-background_accent4_overlay100 group",
            // "bg-background_accent4_overlay100 text-content_inverse_primary",
          )}
          disabled={isGrantEquityPending}
          data-loading={isGrantEquityPending}
          onClick={claimCredits}
        >
          <span className="group-loading:hidden">
            {I18n.t("Claim 500 Credits")}
          </span>
          <Icon
            name="ic_loading"
            size={24}
            className={cn("hidden animate-spin group-loading:block")}
          />
        </button>
        <button
          type="button"
          disabled={isGrantEquityPending}
          data-openreplay-label="member_retentioncreditsoffer_stillcancel"
          data-tracking-eventname="member_subscription_retention_credits_offer_modal_still_cancel_click"
          className="button_s w-full py-3 text-center text-content_tertiary"
          onClick={onStillWantToCancel}
        >
          {I18n.t("Still want to cancel")}
        </button>
      </div>
    </div>
  );
};
